import React, { Fragment } from "react";
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Icon,
} from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import type { NavItem } from "./mainMenu.utils";
import { INACTIVE_COLOR } from "./mainMenu.utils";
import { prefetchLocationMap } from "../pages/locationMap/prefetchLocationMap";

interface ExpandedNavItemProps {
  nav: NavItem;
  isMobile: boolean;
  onMobileClose?: () => void;
  isMenuOpen: (menuText: string, hasActiveChild: boolean) => boolean;
  toggleMenu: (menuText: string, hasActiveChild: boolean) => void;
}

const ExpandedNavItem: React.FC<ExpandedNavItemProps> = ({
  nav,
  isMobile,
  onMobileClose,
  isMenuOpen,
  toggleMenu,
}) => {
  const location = useLocation();

  if (!nav.isParent) {
    const isActive = nav.path ? location.pathname.startsWith(nav.path) : false;
    return (
      <ListItemButton
        component={RouterLink}
        to={nav.path || "/"}
        onClick={() => {
          if (isMobile && onMobileClose) onMobileClose();
        }}
        onMouseEnter={() => {
          if (nav.path === "/locationmap") prefetchLocationMap();
        }}
        sx={{
          my: 0.25,
          px: 1.2,
          py: 0.75,
          borderLeft: "2px solid",
          borderLeftColor: isActive ? "#fff" : "transparent",
          bgcolor: isActive ? "#3b82f6" : "transparent",
          color: isActive ? "#fff" : INACTIVE_COLOR,
          "&:hover": { color: "#fff", bgcolor: "#3b82f6", borderLeftColor: "#fff" },
        }}
      >
        <ListItemIcon sx={{ minWidth: 32, color: "inherit" }}>
          <Icon fontSize="small" className="material-symbols-outlined" sx={{ fontSize: "18px" }}>
            {nav.Icon}
          </Icon>
        </ListItemIcon>
        <ListItemText
          primary={nav.text}
          primaryTypographyProps={{ sx: { color: "inherit", fontSize: "14px" } }}
        />
      </ListItemButton>
    );
  }

  // Parent with collapsible children
  const isParentActive =
    nav.children?.some((child) => location.pathname.startsWith(child.path || "")) ?? false;
  const menuIsOpen = isMenuOpen(nav.text, isParentActive);

  return (
    <Fragment>
      <ListItemButton
        onClick={() => toggleMenu(nav.text, isParentActive)}
        sx={{
          my: 0.5,
          px: 1.2,
          py: 1,
          borderLeft: "2px solid",
          borderLeftColor: isParentActive ? "#3b82f6" : "transparent",
          bgcolor: isParentActive ? "#3b82f6" : "transparent",
          color: isParentActive ? "#fff" : INACTIVE_COLOR,
          "&:hover": { color: "#fff", bgcolor: "#3b82f6", borderLeftColor: "#fff" },
        }}
      >
        <ListItemIcon sx={{ minWidth: 32, color: "inherit" }}>
          <Icon fontSize="small" className="material-symbols-outlined">
            {nav.Icon}
          </Icon>
        </ListItemIcon>
        <ListItemText
          primary={nav.text}
          primaryTypographyProps={{ sx: { color: "inherit", fontSize: "14px" } }}
        />
        {menuIsOpen ? (
          <ExpandLess sx={{ color: "inherit" }} />
        ) : (
          <ExpandMore sx={{ color: "inherit" }} />
        )}
      </ListItemButton>
      <Collapse in={menuIsOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {nav.children?.map((child) => {
            const isChildActive = location.pathname.startsWith(child.path || "");
            return (
              <ListItemButton
                key={child.path}
                component={RouterLink}
                to={child.path || "/"}
                onClick={() => {
                  if (isMobile && onMobileClose) onMobileClose();
                }}
                sx={{
                  pl: 4,
                  py: 1,
                  borderLeft: "2px solid",
                  borderLeftColor: isChildActive ? "#fff" : "transparent",
                  bgcolor: isChildActive ? "#3b82f6" : "transparent",
                  color: isChildActive ? "#fff" : INACTIVE_COLOR,
                  "&:hover": { color: "#fff", bgcolor: "#3b82f6", borderLeftColor: "#fff" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32, color: "inherit" }}>
                  <Icon fontSize="small" className="material-symbols-outlined">
                    {child.Icon}
                  </Icon>
                </ListItemIcon>
                <ListItemText
                  primary={child.text}
                  primaryTypographyProps={{ sx: { color: "inherit" } }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Collapse>
    </Fragment>
  );
};

export default ExpandedNavItem;
